import { Box } from "@mui/material";
import { IObjectAutocomplete } from "shared/utils/hooks/useForm";
import AutocompleteField from "shared/autocomplete-field/autocomplete-field";
import { FC, useEffect, useState, useCallback, useRef } from "react";
import { useAppDispatch, useAppSelector } from "shared/utils/hooks/useRedux";
import { ContractsList } from "entities/insurance-contscts/selector";
import CustomButton from "shared/custom-button/CustomButton";

import ReplayIcon from "@mui/icons-material/Replay";
import { fetchContractList } from "entities/insurance-contscts/actions/fetch-contracts-list";

import styles from "widgets/all-contracts-list/AllContractsList.module.css";

interface IFormData {
  list: string | string[];
}

interface IAllContractsList {
  formData: IFormData;
  handleAutocompeteChange: (
    newValue: IObjectAutocomplete | IObjectAutocomplete[] | null,
    name: string,
    multiple: boolean
  ) => void;
  setContractID: (value: string | string[]) => void;
  multiple: boolean;
  name: string;
  reducerAction?: (payload: any) => any;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
}

export const AllContractsList: FC<IAllContractsList> = ({
  formData,
  handleAutocompeteChange,
  setContractID,
  multiple,
  name,
  reducerAction,
  error,
  helperText,
  disabled,
}) => {
  const dispatch = useAppDispatch();

  const listData = useAppSelector(ContractsList);

  useEffect(() => {
    if (listData.length === 1 && !formData.list) {
      const singleContract = listData[0];
      handleAutocompeteChange(singleContract, name, multiple);
    }
  }, [listData, formData.list, handleAutocompeteChange, multiple, name]);

  useEffect(() => {
    if (Array.isArray(formData.list)) {
      setContractID(formData.list);
    } else if (formData.list) {
      setContractID([formData.list]);
    } else {
      setContractID([]);
    }
  }, [formData.list, setContractID]);

  const renderValue = useCallback(
    (
      val: string | string[],
      objectList: IObjectAutocomplete[]
    ): IObjectAutocomplete[] | IObjectAutocomplete | null => {
      if (Array.isArray(val)) {
        return val
          .map((id) => objectList.find((obj) => obj.ID === id))
          .filter(Boolean) as IObjectAutocomplete[];
      } else if (val) {
        return objectList.find((obj) => obj.ID === val) || null;
      } else {
        return null;
      }
    },
    []
  );

  const handleUpdate = () => {
    dispatch(fetchContractList({ value: true, sortArray: [] }));
  };
  const hasFetched = useRef(false);

  useEffect(() => {
    if (!hasFetched.current && listData.length === 0) {
      dispatch(fetchContractList({ value: true, sortArray: [] }));
      hasFetched.current = true;
    }
  }, [listData, dispatch]);

  if (
    listData.length > 1 ||
    (listData.length === 1 && typeof formData.list === "string")
  ) {
    return (
      <Box>
        <AutocompleteField
          label="Выберите договор"
          objectsList={listData}
          name={name}
          handleAutocompeteChange={(newValue) => {
            handleAutocompeteChange(newValue, name, multiple);
            if (reducerAction) {
              dispatch(reducerAction({ page: 1 }));
            }
          }}
          multiple={multiple}
          value={renderValue(formData.list, listData)}
          error={error}
          helperText={helperText}
          disabled={disabled}
        />
      </Box>
    );
  } else if (listData.length === 1 && Array.isArray(formData.list)) {
    return null;
  } else if (listData.length === 0 && !hasFetched) {
    return (
      <Box className={styles.emptyList}>
        <p>
          Если у вас больше одного договора страхования, но нет поля выбора
          договора, обновите список
        </p>
        <CustomButton
          icon={ReplayIcon}
          leftIcon={true}
          handleClick={handleUpdate}
          title="Обновить"
          textColor="#0054E6"
          backgroundColor="none"
          border="none"
          borderRadius="4px"
          padding="8px 30px"
        />
      </Box>
    );
  }
};

export default AllContractsList;
